<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'

// 定义鱼类属性
const props = defineProps<{
  fish: {
    id: number
    type: {
      name: string
      image: string
      speed: number
      value: number
    }
    x: number
    y: number
    direction: number
    scale: number
    hungry: boolean
  }
}>()

// 鱼的眼睛状态
const eyeState = ref('normal')
const bodyState = ref('normal')

// 计算鱼的样式
const fishStyle = computed(() => {
  return {
    left: `${props.fish.x}px`,
    top: `${props.fish.y}px`,
    transform: `scale(${props.fish.scale}) scaleX(${props.fish.direction})`,
    filter: props.fish.hungry ? 'hue-rotate(0deg)' : 'hue-rotate(120deg)',
  }
})

// 随机眼睛和身体动画
const animateRandomly = () => {
  // 随机眨眼
  if (Math.random() < 0.05) {
    eyeState.value = 'blink'
    setTimeout(() => {
      eyeState.value = 'normal'
    }, 300)
  }

  // 随机身体抽搐（如果饥饿）
  if (props.fish.hungry && Math.random() < 0.1) {
    bodyState.value = 'twitch'
    setTimeout(() => {
      bodyState.value = 'normal'
    }, 500)
  }

  // 随机身体膨胀（如果已进食）
  if (!props.fish.hungry && Math.random() < 0.05) {
    bodyState.value = 'inflate'
    setTimeout(() => {
      bodyState.value = 'normal'
    }, 1000)
  }

  // 持续随机动画
  setTimeout(animateRandomly, 1000 + Math.random() * 2000)
}

// 获取鱼的特定样式
const fishSpecificStyle = computed(() => {
  const styles: Record<string, any> = {}

  // 根据鱼种类添加特定样式
  if (props.fish.type.name === '三眼马桶鱼') {
    styles.borderRadius = '30% 50% 50% 30%'
    styles.boxShadow = '0 0 20px rgba(0, 150, 255, 0.3)'
  } else if (props.fish.type.name === '多头蠕虫鱼') {
    styles.borderRadius = '40% 60% 60% 40% / 60% 30% 70% 40%'
    styles.boxShadow = '0 0 20px rgba(255, 0, 150, 0.3)'
  } else if (props.fish.type.name === '像素幽灵鱼') {
    styles.borderRadius = '60% 40% 40% 60% / 40% 60% 40% 60%'
    styles.boxShadow = '0 0 20px rgba(150, 255, 0, 0.3)'
    styles.opacity = 0.7 + Math.sin(Date.now() / 500) * 0.3
  }

  // 根据身体状态添加额外动画
  if (bodyState.value === 'twitch') {
    styles.animation = 'fishTwitch 0.5s ease'
  } else if (bodyState.value === 'inflate') {
    styles.animation = 'fishInflate 1s ease'
  }

  return styles
})

// 气泡效果
const bubbles = ref<Array<{ id: number; x: number; y: number; size: number }>>([])

const createBubble = () => {
  if (!props.fish.hungry && Math.random() < 0.3) {
    // 吃饱的鱼更容易产生气泡
    const bubble = {
      id: Date.now(),
      x: Math.random() * 40 - 20,
      y: -10 - Math.random() * 20,
      size: 5 + Math.random() * 10,
    }

    bubbles.value.push(bubble)

    // 气泡上升并消失
    setTimeout(() => {
      bubbles.value = bubbles.value.filter((b) => b.id !== bubble.id)
    }, 2000)
  }

  setTimeout(createBubble, 500 + Math.random() * 2000)
}

onMounted(() => {
  animateRandomly()
  createBubble()
})
</script>

<template>
  <div class="fish" :style="fishStyle">
    <div class="fish-body" :style="fishSpecificStyle">
      <!-- 鱼眼睛 -->
      <div class="fish-eyes" :class="[`eyes-${eyeState}`]">
        <div class="eye eye-1"></div>
        <div class="eye eye-2"></div>
        <div v-if="fish.type.name === '三眼马桶鱼'" class="eye eye-3"></div>
      </div>

      <!-- 鱼嘴 -->
      <div class="fish-mouth" :class="{ 'mouth-hungry': fish.hungry }"></div>

      <!-- 鱼鳍 -->
      <div class="fish-fin fin-top"></div>
      <div class="fish-fin fin-bottom"></div>

      <!-- 鱼尾 -->
      <div class="fish-tail"></div>

      <!-- 特殊效果 -->
      <template v-if="fish.type.name === '三眼马桶鱼'">
        <div class="toilet-effect"></div>
      </template>

      <template v-if="fish.type.name === '多头蠕虫鱼'">
        <div class="worm-segment" v-for="i in 3" :key="i"></div>
      </template>

      <template v-if="fish.type.name === '像素幽灵鱼'">
        <div class="ghost-pixels" v-for="i in 5" :key="i"></div>
      </template>

      <!-- 气泡 -->
      <div
        v-for="bubble in bubbles"
        :key="bubble.id"
        class="bubble"
        :style="{
          left: `${bubble.x}px`,
          top: `${bubble.y}px`,
          width: `${bubble.size}px`,
          height: `${bubble.size}px`,
        }"
      ></div>
    </div>
  </div>
</template>

<style scoped>
.fish {
  position: absolute;
  z-index: 3;
  transition:
    top 0.5s ease,
    left 0.5s ease;
}

.fish-body {
  position: relative;
  width: 80px;
  height: 40px;
  background: rgba(100, 180, 255, 0.8);
  border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
  transition: all 0.3s ease;
  overflow: visible;
}

.fish-eyes {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 50px;
  height: 15px;
}

.eye {
  position: absolute;
  width: 10px;
  height: 10px;
  background: white;
  border-radius: 50%;
  top: 0;
}

.eye::after {
  content: '';
  position: absolute;
  width: 5px;
  height: 5px;
  background: black;
  border-radius: 50%;
  top: 2px;
  left: 2px;
}

.eye-1 {
  left: 5px;
}

.eye-2 {
  left: 25px;
}

.eye-3 {
  left: 15px;
  top: -8px;
}

.eyes-blink .eye::after {
  transform: scale(0);
}

.fish-mouth {
  position: absolute;
  left: 5px;
  bottom: 12px;
  width: 15px;
  height: 8px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  transition: all 0.3s ease;
}

.mouth-hungry {
  width: 20px;
  height: 15px;
  animation: mouthOpen 2s infinite;
}

.fish-fin {
  position: absolute;
  width: 20px;
  height: 15px;
  background: rgba(100, 180, 255, 0.6);
}

.fin-top {
  top: -10px;
  left: 40px;
  border-radius: 50% 50% 0 0;
  transform-origin: bottom;
  animation: finWave 2s infinite;
}

.fin-bottom {
  bottom: -10px;
  left: 30px;
  border-radius: 0 0 50% 50%;
  transform-origin: top;
  animation: finWave 2s infinite reverse;
}

.fish-tail {
  position: absolute;
  right: -15px;
  top: 10px;
  width: 20px;
  height: 20px;
  background: rgba(100, 180, 255, 0.7);
  clip-path: polygon(0 0, 0 100%, 100% 50%);
  animation: tailWag 1s infinite;
}

/* 特殊效果 */
.toilet-effect {
  position: absolute;
  right: -20px;
  top: 15px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  animation: flush 2s infinite;
}

.worm-segment {
  position: absolute;
  width: 15px;
  height: 15px;
  background: rgba(255, 100, 180, 0.7);
  border-radius: 50%;
  animation: wormWiggle 3s infinite;
}

.worm-segment:nth-child(1) {
  right: -10px;
  top: 5px;
  animation-delay: 0s;
}

.worm-segment:nth-child(2) {
  right: -25px;
  top: 10px;
  animation-delay: 0.2s;
}

.worm-segment:nth-child(3) {
  right: -40px;
  top: 15px;
  animation-delay: 0.4s;
}

.ghost-pixels {
  position: absolute;
  width: 6px;
  height: 6px;
  background: rgba(150, 255, 100, 0.8);
  animation: pixelFloat 3s infinite;
}

.ghost-pixels:nth-child(1) {
  top: -8px;
  left: 20px;
  animation-delay: 0s;
}

.ghost-pixels:nth-child(2) {
  top: 5px;
  right: -8px;
  animation-delay: 0.5s;
}

.ghost-pixels:nth-child(3) {
  bottom: -8px;
  left: 30px;
  animation-delay: 1s;
}

.ghost-pixels:nth-child(4) {
  top: 15px;
  left: -8px;
  animation-delay: 1.5s;
}

.ghost-pixels:nth-child(5) {
  bottom: 5px;
  right: 10px;
  animation-delay: 2s;
}

.bubble {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
  animation: bubbleRise 2s linear;
}

@keyframes tailWag {
  0% {
    transform: scaleX(1);
  }
  50% {
    transform: scaleX(-0.8);
  }
  100% {
    transform: scaleX(1);
  }
}

@keyframes finWave {
  0% {
    transform: rotate(-10deg);
  }
  50% {
    transform: rotate(10deg);
  }
  100% {
    transform: rotate(-10deg);
  }
}

@keyframes mouthOpen {
  0% {
    height: 8px;
  }
  10% {
    height: 15px;
  }
  30% {
    height: 8px;
  }
  100% {
    height: 8px;
  }
}

@keyframes flush {
  0% {
    opacity: 0.2;
    transform: scale(0.8);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.2);
  }
  100% {
    opacity: 0.2;
    transform: scale(0.8);
  }
}

@keyframes wormWiggle {
  0% {
    transform: translateY(0);
  }
  25% {
    transform: translateY(5px);
  }
  50% {
    transform: translateY(0);
  }
  75% {
    transform: translateY(-5px);
  }
  100% {
    transform: translateY(0);
  }
}

@keyframes pixelFloat {
  0% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(3px, 3px);
  }
  50% {
    transform: translate(0, 6px);
  }
  75% {
    transform: translate(-3px, 3px);
  }
  100% {
    transform: translate(0, 0);
  }
}

@keyframes bubbleRise {
  0% {
    transform: translateY(0);
    opacity: 0.8;
  }
  100% {
    transform: translateY(-100px);
    opacity: 0;
  }
}

@keyframes fishTwitch {
  0% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(15deg);
  }
  50% {
    transform: rotate(-10deg);
  }
  75% {
    transform: rotate(5deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

@keyframes fishInflate {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.3);
  }
  70% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}
</style>
